/**
 * Identificador do cliente no site público de agendamento: o CPF ou o e-mail usado para
 * entrar. Clientes importados de outros aplicativos costumam não ter CPF, então prender a
 * memória ao documento faria justamente eles se identificarem de novo a cada visita.
 *
 * Fica em localStorage, e não em sessionStorage: o cliente que fecha a aba e volta no dia
 * seguinte para marcar de novo não deve ter que digitar o CPF outra vez. O sessionStorage
 * morria junto com a aba, então na prática a identificação era pedida em toda visita.
 *
 * Todo acesso vai em try/catch: navegador em modo anônimo, com cookies de terceiros
 * bloqueados ou com armazenamento cheio lança ao ler ou gravar, e uma exceção aqui derrubaria
 * a página inteira de agendamento por causa de uma conveniência.
 */

const storageKey = (companyUuid) => `public-booking-customer:${companyUuid}`;

/**
 * Documento guardado desta empresa, ou string vazia.
 *
 * Também recupera o valor que tenha ficado no sessionStorage da versão anterior, promovendo-o
 * para o localStorage: quem estava no meio de uma sessão quando a mudança subiu não perde a
 * identificação.
 */
export const getStoredCustomerDocument = (companyUuid) => {
    if (!companyUuid) {
        return '';
    }

    try {
        const stored = localStorage.getItem(storageKey(companyUuid));
        if (stored) {
            return stored;
        }

        const legacy = sessionStorage.getItem(storageKey(companyUuid));
        if (legacy) {
            localStorage.setItem(storageKey(companyUuid), legacy);
            sessionStorage.removeItem(storageKey(companyUuid));
            return legacy;
        }
    } catch (error) {
        // Armazenamento indisponível: o cliente só terá que se identificar de novo.
    }

    return '';
};

/**
 * Guarda o identificador para as próximas visitas: o e-mail como digitado, o CPF só com
 * dígitos. Tirar os não numéricos de um e-mail destruiria o valor guardado.
 */
export const setStoredCustomerDocument = (companyUuid, document) => {
    if (!companyUuid || !document) {
        return;
    }

    try {
        const value = document.includes('@') ? document.trim() : document.replace(/\D/g, '');
        localStorage.setItem(storageKey(companyUuid), value);
    } catch (error) {
        // Sem armazenamento, o fluxo segue normalmente: só não fica lembrado.
    }
};

/** Esquece o cliente. Usado no "trocar cliente" e quando o identificador guardado não existe mais. */
export const clearStoredCustomerDocument = (companyUuid) => {
    if (!companyUuid) {
        return;
    }

    try {
        localStorage.removeItem(storageKey(companyUuid));
        sessionStorage.removeItem(storageKey(companyUuid));
    } catch (error) {
        // Nada a fazer: sem armazenamento não há o que limpar.
    }
};
